/**
 * ERP Writeback settings tab — master switch plus one rule per change type.
 *
 * Its own Platform Admin section rather than a block inside Features & Modules:
 * these settings decide what actually reaches a customer's ERP. A wrong rule
 * either pushes a value the two parties never agreed into the order book, or —
 * because a pending writeback makes the import refuse the ERP's value for that
 * field — freezes the field against the feed until someone drains the queue.
 *
 * Platform staff only (is_superuser or is_staff). The backend enforces the same
 * restriction on write (`WRITEBACK_SETTING_KEYS` in platform_settings_views),
 * so hiding the section is presentation, not the control.
 *
 * Toggle state comes from `writebackRulesEffective` (shipped defaults with the
 * stored sparse overlay), so defaults live only in
 * `purchasing/services/writeback_gate.py`.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  Upload, CheckSquare, XCircle, CalendarClock, Package, Tag, ShieldAlert, Hash,
} from 'lucide-react';
import { FeatureToggle } from './FeatureToggle';
import { PlatformSettings, WritebackRuleKey } from './types';

interface WritebackRulesSectionProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
}

interface RuleCopy {
  key: WritebackRuleKey;
  icon: React.ReactNode;
  label: string;
  description: string;
}

/**
 * Display order and copy for each rule. Keys must match
 * `writeback_gate.WRITEBACK_RULES`; the backend rejects any key it does not
 * recognise, so a typo here fails loudly on save rather than silently storing
 * a rule that is never consulted.
 */
const RULES: RuleCopy[] = [
  {
    key: 'change_odd',
    icon: <CalendarClock className="h-5 w-5 text-primary" />,
    label: 'Agreed delivery date change (supplier asked)',
    description:
      'The supplier proposed a new date and the customer accepted it. Queued when the customer accepts, never when the supplier proposes. Default: on.',
  },
  {
    key: 'change_crd',
    icon: <CalendarClock className="h-5 w-5 text-primary" />,
    label: 'Agreed delivery date change (customer asked)',
    description:
      'The customer proposed a new date and the supplier accepted it. Queued when the supplier accepts, never when the customer proposes. Default: on.',
  },
  {
    key: 'accepted_qty',
    icon: <Package className="h-5 w-5 text-primary" />,
    label: 'Agreed quantity change',
    description:
      'A proposed quantity change that the other side accepted. Default: on.',
  },
  {
    key: 'accepted_unit_price',
    icon: <Tag className="h-5 w-5 text-primary" />,
    label: 'Agreed price change',
    description:
      'A proposed unit price change that the other side accepted. Default: on.',
  },
  {
    key: 'accepted',
    icon: <CheckSquare className="h-5 w-5 text-primary" />,
    label: 'Plain acknowledgement (no change)',
    description:
      'A supplier simply accepts a PO line with no change to date, price or quantity. Most ERPs ignore these — the middleware reports success without updating anything — so they only add queue noise. Default: off.',
  },
  {
    key: 'declined',
    icon: <XCircle className="h-5 w-5 text-primary" />,
    label: "Customer declines the supplier's date",
    description:
      "The customer refused the supplier's proposed date. The refused date is written to the ERP due date and the ODD is not touched. Nothing is written for any other decline: a supplier declining the customer's date, a declined price or quantity, or a decline made in bulk. Turn on only where the middleware writes a decline to the due date (Atlas Copco). Default: off.",
  },
];

/**
 * Renders the ERP Writeback settings tab.
 *
 * @param platformSettings - Current settings, including the effective rule map
 * @param saving - Disables the save button while a save is in flight
 * @param onPlatformSettingsChange - Receives the updated settings object
 * @param onSave - Persists the settings
 * @returns The tab content
 * @link PlatformAdminPage gates this section on is_superuser || is_staff
 */
export function WritebackRulesSection({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
}: WritebackRulesSectionProps) {
  const effective = platformSettings.writebackRulesEffective;
  const masterOn = platformSettings.writebackEnabled;

  const setRule = (key: WritebackRuleKey, checked: boolean) => {
    onPlatformSettingsChange({
      ...platformSettings,
      writebackRulesEffective: { ...effective, [key]: checked },
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            ERP Writeback
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-normal text-muted-foreground">
              <ShieldAlert className="h-3 w-3" />
              Platform staff only
            </span>
          </CardTitle>
          <CardDescription>
            What portal changes get queued for the middleware to apply to the ERP. A change is
            only ever queued once both sides have agreed it — a proposal on its own is never
            written back.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FeatureToggle
            icon={<Upload className="h-5 w-5 text-primary" />}
            label="Write changes back to the ERP"
            description="Master switch. Off means nothing is queued at all, no writeback rows are created, and imports stop holding those fields against the ERP feed. Turn off for installs whose middleware does not collect them. Default: on."
            checked={masterOn}
            onChange={(checked) =>
              onPlatformSettingsChange({ ...platformSettings, writebackEnabled: checked })
            }
          />

          <div className={`pt-4 border-t ${masterOn ? '' : 'opacity-50'}`}>
            <h3 className="font-semibold mb-1">
              Rules — which changes are queued
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {masterOn
                ? 'Each rule controls one change type. Anything left at its default is not stored, so shipped defaults keep applying.'
                : 'Inactive while “Write changes back to the ERP” is off — nothing is queued regardless of these rules.'}
            </p>
            <div className="space-y-4">
              {RULES.map((rule) => (
                <FeatureToggle
                  key={rule.key}
                  icon={rule.icon}
                  label={rule.label}
                  description={rule.description}
                  checked={effective[rule.key] ?? false}
                  onChange={(checked) => setRule(rule.key, checked)}
                  disabled={!masterOn}
                />
              ))}
            </div>
          </div>

          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-1">Monitoring</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Independent of the rules above — this only changes what the Live imports monitor
              displays, never what is queued or sent.
            </p>
            <FeatureToggle
              icon={<Hash className="h-5 w-5 text-primary" />}
              label="Show the ERP row number on the writeback tab"
              description="Adds a SAP Row column beside PO / Line. The Line column is the portal's display position; the ERP addresses rows by its own key, and the two drift apart once a line is deleted or re-sequenced. Turn on when investigating a writeback that landed on the wrong row. Default: off."
              checked={platformSettings.writebackShowSapRow}
              onChange={(checked) =>
                onPlatformSettingsChange({ ...platformSettings, writebackShowSapRow: checked })
              }
            />
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Writeback Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
